import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import AppLayout from '../../components/AppLayout';
import { useAppAuth } from '@/hooks/useAppAuth';
import { createNotification } from '@/hooks/useNotifications';
import { useRecentlyViewed } from '@/hooks/useRecentlyViewed';
import { useBookmarks } from '@/hooks/useBookmarks';
import { MATCHING_ENABLED } from '@/config/features';

type CaregiverData = {
  userId: string;
  name: string;
  gender: string;
  birthDate: string;
  avatarUrl: string | null;
  photoUrl: string | null;
  experienceYears: number;
  hourlyRate: number;
  availableRegions: string[];
  certificates: string[];
  introduction: string;
  availableDays: string[];
  availableTimes: string[];
  isActive: boolean;
  totalMatches: number;
  ratingAvg: number;
  earliestStartDate: string | null;
  careerHistory?: { workplace: string; startDate: string; endDate: string; role: string; description: string }[];
  certificateImages?: { url: string; name: string }[];
};

type ReviewItem = {
  id: string;
  rating: number;
  content: string | null;
  created_at: string;
  reviewer?: { name: string } | null;
};

export default function AppCaregiverDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const auth = useAppAuth(true);
  const { addRecent } = useRecentlyViewed();
  const { isBookmarked, toggleBookmark } = useBookmarks();

  const [cg, setCg] = useState<CaregiverData | null>(null);
  const [cgLoading, setCgLoading] = useState(true);
  const [cgError, setCgError] = useState('');

  const [reviews, setReviews] = useState<ReviewItem[]>([]);
  const [allReviews, setAllReviews] = useState<ReviewItem[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [showAllReviews, setShowAllReviews] = useState(false);

  const [activeTab, setActiveTab] = useState<'profile' | 'reviews'>('profile');

  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestForm, setRequestForm] = useState({
    startDate: '',
    endDate: '',
    location: '',
    dailyHours: '8',
    hourlyRate: 15000,
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const displayReviews = showAllReviews ? allReviews : reviews;

  // Supabase에서 간병인 데이터 로드
  useEffect(() => {
    if (!id) { setCgLoading(false); setCgError('간병인 정보를 찾을 수 없습니다.'); return; }
    loadCaregiver(id);
  }, [id]);

  async function loadCaregiver(userId: string) {
    setCgLoading(true);
    setCgError('');
    try {
      const { data: cgData, error: cgErr } = await supabase
        .from('caregiver_profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (cgErr) throw cgErr;
      if (!cgData) { setCgError('간병인 정보를 찾을 수 없습니다.'); setCgLoading(false); return; }

      const { data: profile, error: profileErr } = await supabase
        .from('profiles')
        .select('name, gender, birth_date, avatar_url')
        .eq('id', userId)
        .maybeSingle();

      if (profileErr) throw profileErr;

      const age = profile?.birth_date
        ? new Date().getFullYear() - new Date(profile.birth_date).getFullYear()
        : 0;

      const mapped: CaregiverData = {
        userId,
        name: profile?.name || '이름 없음',
        gender: profile?.gender || '미입력',
        birthDate: profile?.birth_date || '',
        avatarUrl: profile?.avatar_url || null,
        photoUrl: cgData.photo_url || null,
        experienceYears: cgData.experience_years || 0,
        hourlyRate: cgData.hourly_rate || 15000,
        availableRegions: cgData.available_regions || [],
        certificates: cgData.certificates || [],
        introduction: cgData.introduction || '',
        availableDays: cgData.available_days || [],
        availableTimes: cgData.available_times || [],
        isActive: cgData.is_active ?? false,
        totalMatches: cgData.total_matches || 0,
        ratingAvg: Number(cgData.rating_avg) || 0,
        earliestStartDate: cgData.earliest_start_date || null,
        careerHistory: cgData.career_history || [],
        certificateImages: cgData.certificate_images || [],
      };

      setCg(mapped);
      setRequestForm((prev) => ({ ...prev, hourlyRate: mapped.hourlyRate }));

      // 최근 본 간병인에 추가
      addRecent({
        userId: mapped.userId,
        name: mapped.name,
        avatarUrl: mapped.avatarUrl,
        hourlyRate: mapped.hourlyRate,
        experienceYears: mapped.experienceYears,
        ratingAvg: mapped.ratingAvg,
        regions: mapped.availableRegions,
      });

      // 리뷰 로드
      loadReviews(userId);
    } catch (err) {
      console.error('Failed to load caregiver:', err);
      setCgError('간병인 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setCgLoading(false);
    }
  }

  async function loadReviews(userId: string) {
    setReviewsLoading(true);
    try {
      const { data, error: queryError } = await supabase
        .from('reviews')
        .select('id, rating, content, created_at, reviewer_id')
        .eq('reviewee_id', userId)
        .order('created_at', { ascending: false });

      if (queryError) throw queryError;

      // reviewer 프로필 batch 조회 (N+1 방지)
      const reviewerIds = [...new Set((data || []).map((r) => r.reviewer_id))];
      let profileMap = new Map<string, { name: string }>();
      if (reviewerIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, name')
          .in('id', reviewerIds);
        (profiles || []).forEach((p) => profileMap.set(p.id, { name: p.name }));
      }

      const enriched = (data || []).map((r) => ({
        id: r.id,
        rating: r.rating,
        content: r.content,
        created_at: r.created_at,
        reviewer: profileMap.get(r.reviewer_id) || null,
      }));

      setReviews(enriched.slice(0, 5));
      setAllReviews(enriched);
    } catch (err) {
      console.error('Failed to load reviews:', err);
    } finally {
      setReviewsLoading(false);
    }
  }

  async function handleSubmitRequest() {
    setSubmitError('');
    if (!MATCHING_ENABLED) {
      setSubmitError('현재 매칭 서비스가 일시 중단되었습니다.');
      return;
    }
    if (!requestForm.startDate || !requestForm.endDate || !requestForm.location) {
      setSubmitError('간병 기간과 장소를 모두 입력해주세요.');
      return;
    }
    if (!auth.userId) {
      setSubmitError('로그인이 필요합니다.');
      return;
    }
    if (!cg) return;

    setSubmitting(true);
    try {
      const days = Math.max(1, Math.ceil(
        (new Date(requestForm.endDate).getTime() - new Date(requestForm.startDate).getTime()) / (1000 * 60 * 60 * 24)
      ) + 1);
      const totalAmount = requestForm.hourlyRate * parseInt(requestForm.dailyHours) * days;

      const { error: insertError } = await supabase.from('match_requests').insert({
        requester_id: auth.userId,
        receiver_id: cg.userId,
        requester_role: 'patient',
        receiver_role: 'caregiver',
        status: 'pending',
        start_date: requestForm.startDate,
        end_date: requestForm.endDate,
        location: requestForm.location,
        hourly_rate: requestForm.hourlyRate,
        daily_hours: parseInt(requestForm.dailyHours),
        total_amount: totalAmount,
        notes: requestForm.notes || null,
        contact_revealed: false,
      });

      if (insertError) throw insertError;

      createNotification({
        userId: cg.userId,
        type: 'match_request',
        title: '새로운 매칭 요청이 도착했습니다',
        body: `${cg.name}님, ${requestForm.startDate} ~ ${requestForm.endDate} 간병 요청입니다.`,
      });

      setSubmitSuccess(true);
      setTimeout(() => {
        setShowRequestForm(false);
        setSubmitSuccess(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to submit request:', err);
      setSubmitError('매칭 요청 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  }

  function getAge(birthDate: string): number {
    if (!birthDate) return 0;
    return new Date().getFullYear() - new Date(birthDate).getFullYear();
  }

  function formatDateStr(dateStr: string | null): string {
    if (!dateStr) return '협의 가능';
    return new Date(dateStr).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
  }

  // 로딩
  if (cgLoading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="w-8 h-8 border-2 border-[#2E7D6B] border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  // 에러 또는 없음
  if (cgError || !cg) {
    return (
      <AppLayout>
        <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-gray-50">
          <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-3">
            <i className="ri-error-warning-line text-xl text-gray-400" />
          </div>
          <p className="text-sm text-gray-500">{cgError || '간병인 정보를 찾을 수 없어요'}</p>
          <button
            onClick={() => navigate('/app/patient/search')}
            className="mt-4 px-6 py-2.5 rounded-xl text-sm font-medium text-[#2E7D6B] bg-[#f0f9f6] cursor-pointer whitespace-nowrap"
          >
            검색으로 돌아가기
          </button>
        </div>
      </AppLayout>
    );
  }

  const displayImage = cg.photoUrl || cg.avatarUrl;
  const age = getAge(cg.birthDate);

  return (
    <AppLayout>
      <div className="min-h-screen pb-24 bg-gray-50">
        {/* 상단 이미지 + 뒤로가기 */}
        <div className="relative bg-white">
          <div className="h-48 overflow-hidden">
            {displayImage ? (
              <img
                src={displayImage}
                alt={cg.name}
                className="w-full h-full object-cover object-top"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#E8F5E9] to-[#C8E6C9] flex items-center justify-center">
                <i className="ri-user-3-line text-6xl text-[#2E7D6B]/30" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-transparent" />
          </div>
          <button
            onClick={() => navigate(-1)}
            className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center cursor-pointer"
          >
            <i className="ri-arrow-left-line text-gray-700" />
          </button>
        </div>

        {/* 프로필 정보 */}
        <div className="px-5 -mt-6 relative z-10">
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-extrabold text-gray-900">{cg.name}</h1>
                  {cg.totalMatches >= 10 && cg.ratingAvg >= 4.8 && (
                    <span className="inline-flex items-center gap-1 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[10px] font-extrabold px-2 py-0.5 rounded-full">
                      <i className="ri-verified-badge-fill text-[11px]" />
                      우수간병인
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  {cg.gender} · {age}세 · 경력 {cg.experienceYears}년
                </p>
              </div>
              <div className="flex items-center gap-1 bg-amber-50 px-2.5 py-1 rounded-lg">
                <i className="ri-star-fill text-amber-400 text-sm" />
                <span className="text-sm font-extrabold text-gray-900">
                  {cg.ratingAvg > 0 ? cg.ratingAvg.toFixed(1) : '-'}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-4">
              <span className="text-lg font-extrabold text-[#2E7D6B]">
                {cg.hourlyRate.toLocaleString()}원
              </span>
              <span className="text-xs text-gray-400">/ 시간</span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                {cg.totalMatches}회 매칭
              </span>
              <button
                onClick={() => toggleBookmark(cg.userId)}
                className={`w-9 h-9 rounded-xl flex items-center justify-center cursor-pointer transition-all ml-auto ${
                  isBookmarked(cg.userId) ? 'bg-amber-50 text-amber-500' : 'bg-gray-100 text-gray-400 hover:text-amber-400'
                }`}
              >
                <i className={`${isBookmarked(cg.userId) ? 'ri-bookmark-fill' : 'ri-bookmark-line'} text-lg`} />
              </button>
            </div>

            {cg.introduction && (
              <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 rounded-xl p-4">
                {cg.introduction}
              </p>
            )}

            {/* 탭 네비게이션 */}
            <div className="flex bg-gray-100 rounded-full p-1 mt-4">
              <button
                onClick={() => setActiveTab('profile')}
                className={`flex-1 py-2.5 rounded-full text-sm font-bold cursor-pointer transition-all whitespace-nowrap ${
                  activeTab === 'profile'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <i className="ri-user-star-line mr-1.5" />
                프로필 정보
              </button>
              <button
                onClick={() => setActiveTab('reviews')}
                className={`flex-1 py-2.5 rounded-full text-sm font-bold cursor-pointer transition-all whitespace-nowrap ${
                  activeTab === 'reviews'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <i className="ri-chat-quote-line mr-1.5" />
                리뷰
                {allReviews.length > 0 && (
                  <span className="ml-1 text-[10px]">({allReviews.length})</span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* 탭 컨텐츠: 프로필 정보 */}
        {activeTab === 'profile' && (
          <div className="px-5 mt-3 space-y-3">
            {/* 활동 지역 */}
            {cg.availableRegions.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <h2 className="text-sm font-extrabold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-map-pin-line text-[#2E7D6B]" />
                  활동 가능 지역
                </h2>
                <div className="flex flex-wrap gap-2">
                  {cg.availableRegions.map((r) => (
                    <span key={r} className="text-xs px-3 py-1.5 rounded-full bg-[#f0f9f6] text-[#2E7D6B] font-medium">
                      {r}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* 자격증 */}
            {cg.certificates.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <h2 className="text-sm font-extrabold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-award-line text-[#1976D2]" />
                  보유 자격증
                </h2>
                <div className="flex flex-wrap gap-2">
                  {cg.certificates.map((c) => (
                    <span key={c} className="text-xs px-3 py-1.5 rounded-full bg-blue-50 text-[#1976D2] font-medium">
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* 자격증 이미지 */}
            {cg.certificateImages && cg.certificateImages.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <h2 className="text-sm font-extrabold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-image-line text-[#2E7D6B]" />
                  자격증 이미지
                </h2>
                <div className="flex flex-wrap gap-2">
                  {cg.certificateImages.map((img, i) => (
                    <img key={i} src={img.url} alt={img.name} className="w-24 h-24 rounded-xl object-cover border border-gray-100" />
                  ))}
                </div>
              </div>
            )}

            {/* 경력 상세 */}
            {cg.careerHistory && cg.careerHistory.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <h2 className="text-sm font-extrabold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-briefcase-line text-[#F57C00]" />
                  경력 상세
                </h2>
                <div className="space-y-2">
                  {cg.careerHistory.map((entry, i) => (
                    <div key={i} className="bg-gray-50 rounded-lg px-3 py-2.5">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-xs font-bold text-gray-800">{entry.workplace || '근무처 미입력'}</p>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-[#1976D2] font-medium">{entry.role || '간병인'}</span>
                      </div>
                      {(entry.startDate || entry.endDate) && (
                        <p className="text-[10px] text-gray-400">{entry.startDate || '미입력'} ~ {entry.endDate || '현재'}</p>
                      )}
                      {entry.description && (
                        <p className="text-[11px] text-gray-600 mt-1">{entry.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 근무 가능 시간 */}
            {(cg.availableDays.length > 0 || cg.availableTimes.length > 0) && (
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <h2 className="text-sm font-extrabold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-time-line text-[#F57C00]" />
                  근무 가능 시간
                </h2>
                {cg.availableDays.length > 0 && (
                  <div className="flex gap-1.5 mb-2">
                    {['월','화','수','목','금','토','일'].map((d) => (
                      <div
                        key={d}
                        className={`flex-1 py-2 rounded-lg text-xs font-bold text-center ${
                          cg.availableDays.includes(d)
                            ? 'bg-[#f0f9f6] text-[#2E7D6B]'
                            : 'bg-gray-50 text-gray-300'
                        }`}
                      >
                        {d}
                      </div>
                    ))}
                  </div>
                )}
                {cg.availableTimes.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {cg.availableTimes.map((t) => (
                      <span key={t} className="text-xs px-3 py-1.5 rounded-full bg-orange-50 text-[#F57C00] font-medium">
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* 시작 가능일 */}
            {cg.earliestStartDate && (
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <h2 className="text-sm font-extrabold text-gray-900 mb-1 flex items-center gap-2">
                  <i className="ri-calendar-check-line text-[#2E7D6B]" />
                  가장 빠른 시작 가능일
                </h2>
                <p className="text-base font-extrabold text-[#2E7D6B]">{formatDateStr(cg.earliestStartDate)}</p>
              </div>
            )}
          </div>
        )}

        {/* 리뷰 섹션 (리뷰 탭일 때만 표시) */}
        {activeTab === 'reviews' && (
        <div className="px-5 mt-3" id="reviews-section">
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-extrabold text-gray-900 flex items-center gap-2">
                <i className="ri-chat-quote-line text-[#F57C00]" />
                받은 리뷰
                {allReviews.length > 0 && (
                  <span className="text-xs font-medium text-gray-400">({allReviews.length})</span>
                )}
              </h2>
              {allReviews.length > 0 && (
                <div className="flex items-center gap-2 bg-amber-50 px-3 py-1.5 rounded-lg">
                  <i className="ri-star-fill text-amber-400 text-sm" />
                  <span className="text-sm font-extrabold text-gray-900">
                    {(allReviews.reduce((sum, r) => sum + r.rating, 0) / allReviews.length).toFixed(1)}
                  </span>
                  <span className="text-[10px] text-gray-400">/ 5.0</span>
                </div>
              )}
            </div>

            {/* 별점 분포 */}
            {allReviews.length > 0 && (
              <div className="mb-5 space-y-1.5">
                {[5, 4, 3, 2, 1].map((star) => {
                  const count = allReviews.filter((r) => r.rating === star).length;
                  const pct = Math.round((count / allReviews.length) * 100);
                  return (
                    <div key={star} className="flex items-center gap-2">
                      <span className="text-[11px] text-gray-500 w-8 text-right shrink-0 whitespace-nowrap">{star}점</span>
                      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-400 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-[11px] text-gray-400 w-8 shrink-0 whitespace-nowrap">{count}개</span>
                    </div>
                  );
                })}
              </div>
            )}

            {reviewsLoading ? (
              <div className="flex justify-center py-6">
                <div className="w-6 h-6 border-2 border-[#F57C00] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : displayReviews.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-chat-3-line text-xl text-gray-300" />
                </div>
                <p className="text-sm font-medium text-gray-400">아직 작성된 리뷰가 없습니다</p>
                <p className="text-xs text-gray-300 mt-1">매칭 완료 후 리뷰가 작성됩니다</p>
              </div>
            ) : (
              <div className="space-y-3">
                {displayReviews.map((r) => (
                  <div key={r.id} className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full bg-[#E3F2FD] flex items-center justify-center">
                        <i className="ri-user-3-line text-xs text-[#1976D2]" />
                      </div>
                      <span className="text-sm font-bold text-gray-900">
                        {r.reviewer?.name || '익명'}
                      </span>
                      <span className="text-[10px] text-gray-400 ml-auto">
                        {new Date(r.created_at).toLocaleDateString('ko-KR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </span>
                    </div>
                    <div className="flex items-center gap-0.5 mb-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i
                          key={star}
                          className={`text-sm ${star <= r.rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-gray-300'}`}
                        />
                      ))}
                      <span className="ml-2 text-xs font-bold text-gray-700">{r.rating}점</span>
                    </div>
                    {r.content ? (
                      <p className="text-sm text-gray-600 leading-relaxed">{r.content}</p>
                    ) : (
                      <p className="text-xs text-gray-400 italic">별점만 남긴 리뷰입니다</p>
                    )}
                  </div>
                ))}

                {/* 더보기 / 접기 버튼 */}
                {allReviews.length > 5 && (
                  <button
                    onClick={() => setShowAllReviews(!showAllReviews)}
                    className="w-full mt-2 py-2.5 rounded-xl text-xs font-semibold text-[#1976D2] bg-blue-50 hover:bg-blue-100 transition-colors flex items-center justify-center gap-1.5 cursor-pointer whitespace-nowrap"
                  >
                    {showAllReviews ? (
                      <>
                        <span>리뷰 접기</span>
                        <i className="ri-arrow-up-s-line text-sm" />
                      </>
                    ) : (
                      <>
                        <span>전체 리뷰 {allReviews.length}개 모두 보기</span>
                        <i className="ri-arrow-down-s-line text-sm" />
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        )}

        {/* 매칭 신청 폼 (팝업) */}
        {showRequestForm && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-end justify-center" onClick={() => setShowRequestForm(false)}>
            <div className="bg-white w-full max-w-md rounded-t-3xl p-6 max-h-[85vh] overflow-y-auto animate-slide-up" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-extrabold text-gray-900">매칭 신청하기</h2>
                <button onClick={() => setShowRequestForm(false)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center cursor-pointer">
                  <i className="ri-close-line text-gray-500" />
                </button>
              </div>

              {submitSuccess ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-2xl bg-green-50 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-double-line text-2xl text-[#2E7D6B]" />
                  </div>
                  <p className="text-lg font-extrabold text-gray-900 mb-1">매칭 요청 완료!</p>
                  <p className="text-sm text-gray-500">상대방이 수락하면 연락처가 공개됩니다.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    {displayImage ? (
                      <img src={displayImage} alt={cg.name} className="w-10 h-10 rounded-lg object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-[#E8F5E9] flex items-center justify-center">
                        <i className="ri-user-3-line text-[#2E7D6B]" />
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-bold text-gray-900">{cg.name} 간병인</p>
                      <p className="text-xs text-gray-500">{cg.hourlyRate.toLocaleString()}원/시간</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">시작일 <span className="text-red-400">*</span></label>
                      <input type="date" value={requestForm.startDate} onChange={(e) => setRequestForm({ ...requestForm, startDate: e.target.value })}
                        className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#2E7D6B] bg-white" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">종료일 <span className="text-red-400">*</span></label>
                      <input type="date" value={requestForm.endDate} onChange={(e) => setRequestForm({ ...requestForm, endDate: e.target.value })}
                        className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#2E7D6B] bg-white" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">간병 장소 <span className="text-red-400">*</span></label>
                    <input type="text" value={requestForm.location} onChange={(e) => setRequestForm({ ...requestForm, location: e.target.value })}
                      placeholder="병원명 또는 주소" className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#2E7D6B] bg-white" />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">하루 간병 시간</label>
                      <select value={requestForm.dailyHours} onChange={(e) => setRequestForm({ ...requestForm, dailyHours: e.target.value })}
                        className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#2E7D6B] bg-white">
                        {['4','6','8','10','12','24'].map((h) => (
                          <option key={h} value={h}>{h}시간</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">시간당 비용</label>
                      <div className="px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm font-bold text-[#2E7D6B]">
                        {requestForm.hourlyRate.toLocaleString()}원
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">요청사항</label>
                    <textarea value={requestForm.notes} onChange={(e) => setRequestForm({ ...requestForm, notes: e.target.value })}
                      placeholder="환자 상태, 특별 요청사항 등을 적어주세요" rows={3} maxLength={500}
                      className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#2E7D6B] bg-white resize-none" />
                  </div>

                  {requestForm.startDate && requestForm.endDate && (
                    <div className="bg-[#f0f9f6] rounded-xl p-3">
                      <p className="text-xs text-gray-500">예상 간병비</p>
                      <p className="text-lg font-extrabold text-[#2E7D6B]">
                        {(requestForm.hourlyRate * parseInt(requestForm.dailyHours || '8') *
                          Math.max(1, Math.ceil(
                            (new Date(requestForm.endDate).getTime() - new Date(requestForm.startDate).getTime()) / (1000 * 60 * 60 * 24)
                          ) + 1)
                        ).toLocaleString()}원
                      </p>
                      <p className="text-[10px] text-gray-400 mt-0.5">간병비는 간병인과 직접 정산합니다</p>
                    </div>
                  )}

                  {submitError && (
                    <p className="text-xs text-red-500 text-center">{submitError}</p>
                  )}

                  <button
                    onClick={handleSubmitRequest}
                    disabled={submitting}
                    className="w-full py-3.5 rounded-xl text-sm font-bold text-white cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 whitespace-nowrap"
                    style={{ backgroundColor: '#2E7D6B' }}
                  >
                    {submitting ? '요청 중...' : '매칭 요청하기'}
                  </button>

                  <p className="text-[10px] text-gray-400 text-center px-4">
                    매칭이 수락되면 상대방의 연락처가 공개되며, 간병비는 당사자 간 직접 정산합니다.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 하단 매칭 버튼 */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-4 z-40">
          <div className="max-w-md mx-auto">
            {MATCHING_ENABLED ? (
              <button
                onClick={() => setShowRequestForm(true)}
                className="w-full py-3.5 rounded-xl text-sm font-bold text-white cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap"
                style={{ backgroundColor: '#2E7D6B' }}
              >
                <i className="ri-send-plane-line mr-2" />
                매칭 신청하기
              </button>
            ) : (
              <div className="w-full py-3.5 rounded-xl text-sm font-bold text-center bg-gray-100 text-gray-400">
                <i className="ri-information-line mr-2" />
                매칭 서비스 준비중입니다
              </div>
            )}
          </div>
        </div>

        <style>{`
          @keyframes slide-up {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }
          .animate-slide-up {
            animation: slide-up 0.3s ease-out;
          }
        `}</style>
      </div>
    </AppLayout>
  );
}